import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConfigProvider, useConfig } from "./context/ConfigContext";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import SiteHead from "./components/SiteHead";

// Pages
import LandingPage from "./pages/LandingPage";
import ReviewsPage from "./pages/ReviewsPage";
import AuthCallback from "./pages/AuthCallback";
import MagicVerify from "./pages/MagicVerify";
import ClientDashboard from "./pages/ClientDashboard";
import ClientProfile from "./pages/ClientProfile";
import ClientPaymentPage from "./pages/ClientPaymentPage";
import RecurringSlotPage from "./pages/RecurringSlotPage";
import ReservationPage from "./pages/ReservationPage";
import ReservationDetail from "./pages/ReservationDetail";
import AdminDashboard from "./pages/AdminDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import ContainersPage from "./pages/ContainersPage";
import SapPage from "./pages/SapPage";
import SapPagePreview from "./pages/SapPagePreview";
import LegalPage from "./pages/LegalPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import MenusPage from "./pages/MenusPage";
import TarifsPage from "./pages/TarifsPage";
import ZonePage from "./pages/ZonePage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#A5BBA8]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'employee') {
      return <Navigate to="/employee" replace />;
    } else {
      return <Navigate to="/client" replace />;
    }
  }

  return children;
}

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - handle before normal routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<MagicVerify />} />
      <Route path="/contenants-conservation" element={<ContainersPage />} />
      <Route path="/services-a-la-personne" element={<SapPage />} />
      <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
      <Route path="/menus" element={<MenusPage />} />
      <Route path="/tarifs" element={<TarifsPage />} />
      <Route path="/zone-intervention" element={<ZonePage />} />
      <Route path="/a-propos" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/avis" element={<ReviewsPage />} />
      <Route path="/sap" element={<SapPage />} />
      <Route path="/credit-impot" element={<SapPage />} />
      <Route path="/avance-immediate" element={<SapPage />} />
      <Route path="/sap-preview" element={<SapPagePreview />} />
      
      {/* Legal pages */}
      <Route path="/mentions-legales" element={<LegalPage pageType="legal_mentions" />} />
      <Route path="/cgv" element={<LegalPage pageType="cgv" />} />
      <Route path="/cgu" element={<LegalPage pageType="cgu" />} />
      <Route path="/confidentialite" element={<LegalPage pageType="privacy_policy" />} />
      <Route path="/cookies" element={<LegalPage pageType="cookies_policy" />} />

      {/* Client routes */}
      <Route 
        path="/client" 
        element={
          <ProtectedRoute allowedRoles={['client', 'admin']}>
            <ClientDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/profile" 
        element={
          <ProtectedRoute allowedRoles={['client', 'admin']}>
            <ClientProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/payment" 
        element={
          <ProtectedRoute allowedRoles={['client', 'admin']}>
            <ClientPaymentPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/recurring" 
        element={
          <ProtectedRoute allowedRoles={['client', 'admin']}>
            <RecurringSlotPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/reservation" 
        element={
          <ProtectedRoute allowedRoles={['client', 'admin']}>
            <ReservationPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/client/reservations/:reservationId" 
        element={
          <ProtectedRoute allowedRoles={['client', 'admin']}>
            <ReservationDetail />
          </ProtectedRoute>
        } 
      />

      {/* Admin routes */}
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Employee routes */}
      <Route 
        path="/employee" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'admin']}>
            <EmployeeDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ConfigProvider>
          <SiteHead />
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" />
          </AuthProvider>
        </ConfigProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
